import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import { IndexPath } from '@/Config/app/router/index.path';
import { Usage } from '@/Domain/api/Subscription/dto/SubscriptionUsage.dto';
import CircularProgress from '@/Domain/components/Shared/CircularProgress/CircularProgress';
import { useThemeStore } from '@/Domain/store/theme.store';

const getProgressColor = (percent: number) => {
  if (percent >= 100) return 'bg-red-600';
  if (percent >= 90) return 'bg-orange-500';
  if (percent >= 75) return 'bg-amber-500';
  return 'bg-neutral-900';
};

const getTextColor = (percent: number) => {
  if (percent >= 100) return 'text-red-600';
  if (percent >= 90) return 'text-orange-500';
  if (percent >= 75) return 'text-amber-500';
  return 'text-neutral-500';
};

type Props = {
  subscription: any;
  open: boolean;
};

export const TierPlanCard = ({ open, subscription }: Props) => {
  const { data } = useQuery<Usage>({ queryKey: ['usage'] });
  const isDark = useThemeStore((state) => state.isDarkTheme());

  const messagesUsed = data?.messages_used ?? 0;
  const messagesLimit = data?.messages_limit ?? 1;
  const messagesRemaining = Math.max(0, messagesLimit - messagesUsed);
  const percentUsed = Math.min(100, (messagesUsed / messagesLimit) * 100);

  const { t } = useTranslation(['subscription', 'common', 'sidebar_menu']);
  const navigate = useNavigate();

  // Paid plans renew on the next payment date; free plans refresh AI credits on messages_next_refreshed.
  const nextPaymentOn = subscription?.subscription?.next_payment_on;
  const renewalDate = nextPaymentOn ?? data?.messages_next_refreshed;
  const formattedRenewalDate = (() => {
    if (!renewalDate) return null;
    const date = new Date(renewalDate);
    return isNaN(date.getTime())
      ? null
      : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  })();

  const handleClick = () => navigate(IndexPath.subscription);

  // Collapsed state - show only circular progress
  if (!open) {
    return (
      <button
        onClick={handleClick}
        className="my-1.5 mx-auto flex items-center justify-center cursor-pointer"
      >
        <CircularProgress
          maxCount={Number(messagesLimit)}
          currentCount={Number(messagesUsed)}
          color={
            percentUsed >= 100
              ? '#dc2626'
              : percentUsed >= 90
                ? '#f97316'
                : percentUsed >= 75
                  ? '#f59e0b'
                  : isDark
                    ? '#d4d4d4'
                    : '#171717'
          }
        />
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="p-2.5 rounded-xl w-full bg-muted border border-border hover:bg-accent transition-colors cursor-pointer text-left"
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-neutral-900">
          {t('sidebar_menu:free_tier.credits')}
        </span>
        <div className="flex items-center gap-0.5 text-xs text-neutral-500">
          <span className="font-medium">{messagesRemaining.toLocaleString()} left</span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full bg-muted-foreground/15 rounded-full overflow-hidden mb-1.5">
        <div
          className={classNames(
            'h-full rounded-full transition-all duration-500 ease-out',
            getProgressColor(percentUsed),
          )}
          style={{ width: `${percentUsed}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className={classNames('font-medium', getTextColor(percentUsed))}>
          {Math.round(percentUsed)}% {t('sidebar_menu:free_tier.used')}
        </span>
        <span className="text-neutral-400 font-medium">{messagesLimit.toLocaleString()}</span>
      </div>

      {/* Renewal info */}
      <p className="text-[10px] text-neutral-400 flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-neutral-400" />
        {formattedRenewalDate
          ? t('sidebar_menu:free_tier.refreshes_on', { date: formattedRenewalDate })
          : t('sidebar_menu:free_tier.refresh_monthly')}
      </p>
    </button>
  );
};
